import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel } from "@/components/Panel";
import { Input } from "@/components/ui/input";
import { descargarPdf, numTrabajo, fmtFecha } from "@/pages/TrabajosActivos";
import { Search, Car, FileText, FileDown, Wrench } from "lucide-react";

const ESTADO_BADGE = {
  finalizado: "bg-emerald-50 text-emerald-700",
  activo: "bg-indigo-50 text-indigo-700",
  anulado: "bg-slate-100 text-slate-500",
};

export default function VehiculoHistorial() {
  const { tienePermiso, isAdmin } = useAuth();
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [vehiculo, setVehiculo] = useState(null);
  const [trabajos, setTrabajos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [cfgFotos, setCfgFotos] = useState(null);
  const [fotosSalidaFaltan, setFotosSalidaFaltan] = useState({});
  const boxRef = useRef(null);

  useEffect(() => { api.get("/config-fotos").then((r) => setCfgFotos(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const finalizados = trabajos.filter((t) => t.estado === "finalizado");
    if (!cfgFotos?.fotos_exigir_salida_para_boleta || !tienePermiso("trabajos.fotos") || !finalizados.length) {
      setFotosSalidaFaltan({}); return;
    }
    const activadoEn = cfgFotos.fotos_exigir_salida_activado_en;
    const candidatos = finalizados.filter((t) => !activadoEn || (t.fecha_finalizacion || "") >= activadoEn);
    let activo = true;
    Promise.all(candidatos.map((t) => api.get(`/trabajos/${t.id}/fotos`)
      .then(({ data }) => [t.id, data?.faltantes?.salida || []]).catch(() => [t.id, []])))
      .then((pares) => {
        if (!activo) return;
        const mapa = {};
        pares.forEach(([id, faltan]) => { if (faltan.length) mapa[id] = faltan; });
        setFotosSalidaFaltan(mapa);
      });
    return () => { activo = false; };
  }, [trabajos, cfgFotos, tienePermiso]);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setResultados([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/vehiculos", { params: { q: term, page: 1, page_size: 10 } });
      setResultados(data.items || []); setAbierto(true);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const fn = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const elegir = async (v) => {
    setVehiculo(v); setAbierto(false); setQ(`${v.placa} — ${v.modelo?.nombre_mostrar || v.marca || ""}`);
    setCargando(true);
    try {
      const { data } = await api.get("/trabajos", { params: { vehiculo_id: v.id } });
      setTrabajos(data);
    } finally { setCargando(false); }
  };

  return (
    <div className="space-y-5" data-testid="vehiculo-historial-page">
      <Panel titulo="Historial de Vehículo" testId="vehiculo-historial-panel">
        <p className="text-sm text-slate-500 mb-3">Busque un vehículo por placa, chasis o dueño para ver su línea de tiempo de trabajos e informes.</p>
        <div className="relative max-w-lg" ref={boxRef}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="historial-buscar-input" value={q} onChange={(e) => { setQ(e.target.value); setVehiculo(null); }}
            onFocus={() => resultados.length && setAbierto(true)} placeholder="Buscar vehículo por placa, chasis o dueño..." className="pl-9" />
          {abierto && resultados.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-64 overflow-y-auto">
              {resultados.map((v) => (
                <button key={v.id} type="button" data-testid={`historial-res-${v.id}`} onClick={() => elegir(v)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                  <Car size={14} className="text-slate-400" />
                  <span className="font-semibold text-slate-700">{v.placa}</span>
                  <span className="text-slate-500 truncate">{v.modelo?.nombre_mostrar || v.marca} · {v.cliente?.nombre || "—"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Panel>

      {vehiculo && (
        <Panel titulo={`Trabajos de ${vehiculo.placa}`} testId="historial-timeline-panel">
          {cargando && <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p>}
          {!cargando && trabajos.length === 0 && (
            <p data-testid="historial-vacio" className="text-sm text-slate-400 py-6 text-center">Este vehículo no tiene trabajos registrados.</p>
          )}
          <div className="relative pl-6">
            {trabajos.map((t) => (
              <div key={t.id} data-testid={`historial-trabajo-${t.id}`} className="relative pb-6 last:pb-0">
                <span className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-indigo-50" />
                <span className="absolute -left-[16px] top-4 bottom-0 w-px bg-slate-200" />
                <div className="rounded-xl border border-slate-200 p-3.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">{numTrabajo(t.numero)}</span>
                    <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded ${ESTADO_BADGE[t.estado] || "bg-slate-100 text-slate-600"}`}>{t.estado}</span>
                    <span className="text-xs text-slate-400 ml-auto">Ingreso: {fmtFecha(t.fecha_ingreso)}{t.fecha_finalizacion ? ` · Fin: ${fmtFecha(t.fecha_finalizacion)}` : ""}</span>
                  </div>
                  {(t.tareas || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {t.tareas.map((ta, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">
                          <Wrench size={11} className="text-slate-400" /> {ta.servicio?.nombre || ta.servicio || ta.servicio_nombre || ta.nombre || "Servicio"}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2.5 flex gap-2">
                    <button data-testid={`historial-boleta-ingreso-${t.id}`} title="Boleta de ingreso"
                      onClick={() => descargarPdf(`/trabajos/${t.id}/boleta-pdf`, `boleta_ingreso_${numTrabajo(t.numero)}.pdf`)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-full">
                      <FileText size={13} /> Boleta ingreso
                    </button>
                    {t.estado === "finalizado" && (
                      <button data-testid={`historial-boleta-salida-${t.id}`}
                        title={fotosSalidaFaltan[t.id] && !isAdmin
                          ? `Faltan fotos de salida: ${fotosSalidaFaltan[t.id].join(", ")}`
                          : "Boleta de salida"}
                        disabled={!!fotosSalidaFaltan[t.id] && !isAdmin}
                        onClick={() => descargarPdf(`/trabajos/${t.id}/reporte-final-pdf`, `reporte_final_${numTrabajo(t.numero)}.pdf`)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-full disabled:opacity-40 disabled:cursor-not-allowed">
                        <FileDown size={13} /> Boleta salida
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
